// admin.onlytrade.ai/voice-dialog — Voice Dialog Editor
//
// Master-admin only. Edits the declarative dialog-graph .jsonc files at
// /opt/onlytrade/data/voice-dialog-graph/ that drive Alice's conversation
// with engineers. Currently lifted sections: cert_safety, defects, signoff.
//
// Backend API: /admin/voice-dialog/* (routes/admin-voice-dialog.js)
//
// Author: Claude (under Daniel Howells direction, 14 May 2026)

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, previewDoc, renderDocBlobUrl } from '../api';
import { useToast } from '../lib/toast';

// ────────────────────────────────────────────────────────────────────
// Types — match the backend's JSON shape
// ────────────────────────────────────────────────────────────────────
type SectionSummary = {
  id: string;
  label: string;
  order: number;
  locked: boolean;
  version: string;
  verified: string | null;
  node_count: number;
  fields: string[];
  error?: string;
};

type NodeMatcher = {
  pattern: string;
  value?: string;
  ack?: string;
  actions?: { type: string; [k: string]: unknown }[];
  next_state?: string;
};

type NodeFreeText = { field: string; max?: number; ack?: string };

type NodeCaptures = { field: string | null; type?: string; matchers?: string };

type NodePrompt = { default?: string; retry?: string; editable_id?: string | null };

type NodeOnFirstAsk = { actions?: { type: string; [k: string]: unknown }[] };

type NodeWhenActive = unknown;

type SectionNode = {
  type: 'ask' | 'say' | 'goto' | 'branch';
  prompt?: NodePrompt;
  retry?: string;
  matchers?: NodeMatcher[] | string;
  free_text?: NodeFreeText;
  captures?: NodeCaptures;
  on_first_ask?: NodeOnFirstAsk;
  when_active?: NodeWhenActive;
  next?: string;
  next_state?: string;
  text?: string;
};

type SectionConfig = {
  section: string;
  label: string;
  order: number;
  locked?: boolean;
  version?: string;
  verified?: string;
  fires_when?: unknown;
  is_done_when?: unknown;
  entry?: string;
  nodes: Record<string, SectionNode>;
};

type SectionDetail = {
  id: string;
  raw: string;
  parsed: SectionConfig;
};

type WordingRegistryEntry = { section: string; label: string; default: string };
type WordingsData = {
  registry: Record<string, WordingRegistryEntry>;
  overrides: Record<string, string>;
  locks: Record<string, unknown>;
};
type HistoryItem = { filename: string; snapshot_ts: string; size: number; mtime: string };

type CertField = {
  id: string;
  label?: string;
  type?: string;
  required?: boolean;
  voice_prompt?: string | null;
  customer_visible?: boolean;
  options?: string[];
};
type CertSection = {
  id: string;
  letter?: string;
  label: string;
  fields?: CertField[];
  fields_per_appliance?: CertField[];
  type?: string;
};
type CertSchema = {
  id: string;
  label: string;
  sections: CertSection[];
};

type LookupSummary = { name: string; label: string; kind: string; path: string; size: number | null; exists: boolean };
type LookupDetail  = { name: string; label: string; kind: string; path: string; parsed: unknown };
type LookupsApiResponse = { lookups: LookupSummary[] };

// Sample data for the Cert Preview tab — represents a typical completed
// cert. Used purely for visual reference in the editor; never persisted.
const SAMPLE_CERT_DATA: Record<string, string> = {
  // registered_business
  gas_safe_reg_no:      '123456',
  engineer_name:        'Daniel Howells',
  gas_safe_licence:     '789012',
  business_name:        'Direct Plumbing Supplies Ltd',
  business_address:     'Unit 4, Phoenix Terrace, Otex Avenue, Rustington',
  business_postcode:    'BN16 3LN',
  business_phone:       '01903 123456',
  // site_details
  property_address:     '25 Oakcroft Gardens, Littlehampton, West Sussex',
  property_postcode:    'BN17 6LT',
  property_type:        'landlord',
  // landlord_or_agent
  landlord_name:        'Paul Lambert',
  landlord_phone:       '07999 596354',
  landlord_address:     '12 The Avenue, Worthing',
  landlord_postcode:    'BN14 9NX',
  landlord_email:       'paul.lambert@example.com',
  // tenant
  tenant_name:          'Daniel Howells',
  tenant_phone:         '07999 596354',
  tenant_email:         'daniel@example.com',
  // defects
  defect_details:       'None',
  remedial_action:      '',
  risk_class:           '',
  // gas_installation_checks
  pipework_visual:      'Pass',
  supply_visual:        'Pass',
  ecv_access:           'Pass',
  equipotential_bonding:'Pass',
  gas_installation_labelled: 'Pass',
  adequate_ventilation: 'Pass',
  smoke_alarm_fitted:   'Yes',
  co_alarm_fitted:      'Yes',
  tightness_test:       'Pass',
  tightness_gauge_type: 'Digital',
  tightness_edition:    'Ed 3',
  tightness_smell_of_gas:'No',
  tightness_install_status:'Existing',
  tightness_meter_type: 'Diaphragm (U6/G4)',
  tightness_pipe_diameter:'≤28 mm',
  tightness_let_by_result:'Pass',
  tightness_start_pressure:'20.5',
  tightness_end_pressure: '19.5',
  tightness_drop_mbar:  '1.0',
  // overall_safety
  full_installation_safe: 'Yes',
  // sign_off
  date_checked:         '2026-05-14',
  next_check_date:      '2027-05-14',
  engineer_signature:   '(signed)',
  received_by:          'Daniel Howells',
  received_by_role:     'Tenant',
};

// Per-appliance sample (rendered inside appliances_inspected section)
const SAMPLE_APPLIANCE: Record<string, string> = {
  location:            'Kitchen',
  type:                'Boiler',
  manufacturer:        'Ideal',
  model:               'Logic Plus c24',
  serial_number:       '0624172987B',
  flue_type:           'Room Sealed',
  installation_date:   '2018-04-12',
  operating_pressure:  '20',
  heat_input_kw:       '24',
  co_ratio:            '0.001',
  owned_by_landlord:   'Yes',
  inspected:           'Yes',
  safety_device_operation: 'Pass',
  ventilation:         'Pass',
  chimney_visual:      'Pass',
  flue_operation:      'Pass',
  serviced:            'No',
  co_alarm_fitted:     'Yes',
  co_alarm_tested:     'Pass',
  safe_to_use:         'Yes',
};

// Trigger rules — which captured values cascade into child certs.
// Mirrors the spawn_cert actions in routes/voice.js.
type TriggerRule = { whenValue: string; spawns: string; severity: 'warning' | 'info'; reason?: string };
const TRIGGER_RULES: Record<string, TriggerRule[]> = {
  tightness_test: [
    { whenValue: 'Fail',  spawns: 'Warning Notice cert', severity: 'warning',
      reason: 'Tightness FAIL → unsafe → auto-spawn AR Warning Notice (TT-001)' },
  ],
  risk_class: [
    { whenValue: 'Immediately Dangerous', spawns: 'Warning Notice (ID)', severity: 'warning',
      reason: 'GIUSP Immediately Dangerous → engineer-issued at-risk notice' },
    { whenValue: 'At-Risk',               spawns: 'Warning Notice (AR)', severity: 'warning',
      reason: 'GIUSP At-Risk → engineer-issued at-risk notice' },
  ],
  safe_to_use: [
    { whenValue: 'No', spawns: 'Warning Notice + label appliance', severity: 'warning',
      reason: 'Unsafe to use → label appliance + customer warning notice' },
  ],
  full_installation_safe: [
    { whenValue: 'No', spawns: 'Warning Notice (installation-wide)', severity: 'warning',
      reason: 'Overall installation unsafe' },
  ],
  serviced: [
    { whenValue: 'Yes', spawns: 'Service Report cert', severity: 'info',
      reason: "Engineer chose to service the appliance → spawns the manufacturer's annual service walk as a child cert" },
  ],
  co_alarm_fitted: [
    { whenValue: 'No', spawns: 'Recommended: Warning Notice (NCS) — not auto-spawned', severity: 'info',
      reason: 'Missing CO alarm is Not to Current Standards. Currently logged but not auto-cascaded.' },
  ],
  smoke_alarm_fitted: [
    { whenValue: 'No', spawns: '(no auto cert — engineer-led advice)', severity: 'info',
      reason: 'Smoke alarm is property-level safety, not Gas Safe scope. Recorded but no auto-spawn.' },
  ],
};

type WordingsApiResponse = WordingsData;
type HistoryApiResponse = { history: HistoryItem[] };

type TestResponse = {
  ok: boolean;
  section_id: string;
  response: {
    speech?: string;
    actions?: { type: string; [k: string]: unknown }[];
    next_state?: string;
    finished?: boolean;
    pending_required?: unknown[];
  } | null;
};

type TestScenario = {
  label: string;
  snap: Record<string, unknown>;
  msg?: string;
  control?: 'start' | 'continue';
};

// ════════════════════════════════════════════════════════════════════════
//  LIST PAGE
// ════════════════════════════════════════════════════════════════════════
export function VoiceDialogListPage() {
  const [items, setItems] = useState<SectionSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let abandoned = false;
    api<{ sections: SectionSummary[] }>('/admin/voice-dialog/sections')
      .then(r => { if (!abandoned) setItems(r.sections); })
      .catch(e => { if (!abandoned) setError(String(e.message || e)); });
    return () => { abandoned = true; };
  }, []);

  return (
    <div className="p-6 max-w-5xl">
      <div className="flex items-center gap-3 mb-2">
        <h1 className="text-2xl font-semibold">Voice Dialog Editor</h1>
        <span className="text-xs px-2.5 py-1 rounded-full bg-orange-50 text-orange-700 border border-orange-200">master_admin only</span>
        <Link to="/voice-dialog/flow" className="ml-auto px-3 py-1.5 text-sm bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50">
          🌐 Flow Visualiser
        </Link>
      </div>
      <p className="text-sm text-gray-600 mb-6">
        Edit the declarative dialog graph that drives Alice's conversation with engineers.
        Each section is a separate <code className="font-mono bg-gray-100 px-1 rounded">.jsonc</code> file on the server.
        Changes are live within seconds — no service restart needed.
      </p>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded p-3 text-sm mb-4">
          {error}
        </div>
      )}

      {!items && !error && (
        <div className="text-sm text-gray-500">Loading sections…</div>
      )}

      {items && items.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded p-3 text-sm">
          No .jsonc files found at <code>/opt/onlytrade/data/voice-dialog-graph/</code>. Lift the first section to populate this list.
        </div>
      )}

      <div className="grid gap-3">
        {items?.map(s => (
          <Link
            key={s.id}
            to={`/voice-dialog/${s.id}`}
            className="block bg-white border border-gray-200 rounded-lg p-4 hover:border-orange-400 hover:shadow-sm transition"
          >
            <div className="flex items-center gap-3 mb-1">
              <span className="font-mono text-orange-700 font-semibold">{s.id}</span>
              {s.locked && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-50 text-green-800 border border-green-300">
                  🔒 locked · {s.version}
                </span>
              )}
              {!s.locked && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-50 text-amber-800 border border-amber-300">
                  unlocked
                </span>
              )}
              <span className="ml-auto text-xs text-gray-500">order {s.order}</span>
            </div>
            <div className="text-base text-gray-900 font-medium">{s.label}</div>
            <div className="mt-1 text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
              <span>{s.node_count} node{s.node_count === 1 ? '' : 's'}</span>
              {s.verified && <span>verified {s.verified}</span>}
              {s.fields.length > 0 && (
                <span>captures: <span className="font-mono">{s.fields.join(', ')}</span></span>
              )}
            </div>
            {s.error && (
              <div className="mt-2 text-xs text-red-700">⚠ parse error: {s.error}</div>
            )}
          </Link>
        ))}
      </div>

      <h2 className="text-lg font-semibold mt-10 mb-3 text-gray-800">Reference data</h2>
      <p className="text-sm text-gray-600 mb-4">Read-only lookups Alice uses at runtime — IGEM regulatory tables, synonym pools, section locks. Click to view.</p>
      <LookupsList />
    </div>
  );
}

// ── Reference-data list (used inside VoiceDialogListPage) ────────────
function LookupsList() {
  const [items, setItems] = useState<LookupSummary[] | null>(null);
  useEffect(() => {
    api<LookupsApiResponse>('/admin/voice-dialog/lookups')
      .then(r => setItems(r.lookups))
      .catch(e => console.warn('lookups load failed:', e));
  }, []);
  if (!items) return <div className="text-sm text-gray-500">Loading lookups…</div>;
  return (
    <div className="grid gap-2">
      {items.map(L => (
        <Link key={L.name} to={`/voice-dialog/lookups/${L.name}`}
              className="block bg-white border border-gray-200 rounded-lg p-3 hover:border-blue-400 hover:shadow-sm transition">
          <div className="flex items-center gap-3">
            <span className="font-mono text-xs text-blue-700 font-semibold">{L.name}</span>
            <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-800 border border-blue-200 rounded">{L.kind}</span>
            <span className="ml-auto text-xs text-gray-500">{L.exists ? `${L.size}B` : 'missing'}</span>
          </div>
          <div className="text-sm text-gray-900 mt-1">{L.label}</div>
        </Link>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
//  LOOKUP VIEWER PAGE
// ════════════════════════════════════════════════════════════════════════
export function LookupViewerPage() {
  const { name } = useParams<{ name: string }>();
  const [detail, setDetail] = useState<LookupDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!name) return;
    api<LookupDetail>(`/admin/voice-dialog/lookups/${name}`)
      .then(d => setDetail(d))
      .catch(e => setError(String(e.message || e)));
  }, [name]);
  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-800 rounded p-3 text-sm">{error}</div>
        <Link to="/voice-dialog" className="text-sm text-orange-700 hover:underline mt-3 inline-block">← Back to sections</Link>
      </div>
    );
  }
  if (!detail) return <div className="p-6 text-sm text-gray-500">Loading {name}…</div>;
  return (
    <div className="p-6 max-w-5xl">
      <div className="text-xs text-gray-500 mb-2">
        <Link to="/voice-dialog" className="hover:text-orange-700 hover:underline">smart_gas_safety</Link>
        {' › '}
        <span className="text-gray-800 font-semibold">lookups / {name}</span>
      </div>
      <div className="flex items-center gap-3 mb-1">
        <h1 className="text-2xl font-semibold">{detail.label}</h1>
        <span className="text-xs px-2.5 py-1 bg-blue-50 text-blue-800 border border-blue-300 rounded-full">{detail.kind}</span>
        <span className="text-xs px-2.5 py-1 bg-gray-50 text-gray-700 border border-gray-200 rounded-full">read-only</span>
      </div>
      <div className="text-sm text-gray-600 mb-5">
        <span className="font-mono text-xs">{detail.path}</span>
      </div>
      <div className="bg-blue-50 border-l-4 border-blue-500 text-blue-900 rounded p-3 text-sm mb-4">
        Read-only view. Edit these files on the server directly (or via a follow-up tab — not yet built).
        Cache: Alice picks up changes within 2 seconds of file mtime change.
      </div>
      <pre className="bg-gray-900 text-gray-100 border border-gray-700 rounded p-4 text-xs font-mono overflow-x-auto leading-relaxed" style={{ maxHeight: '70vh' }}>
        {JSON.stringify(detail.parsed, null, 2)}
      </pre>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
//  FLOW VISUALISER PAGE
// ════════════════════════════════════════════════════════════════════════
//  Top-down visual map of the cert flow. Each section is a card showing
//  its order, kind (declarative/compute), lock state, node count, and a
//  short hint on when it fires. Click → opens that section's editor.
export function VoiceDialogFlowPage() {
  const [items, setItems] = useState<SectionSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api<{ sections: SectionSummary[] }>('/admin/voice-dialog/sections')
      .then(r => setItems(r.sections))
      .catch(e => setError(String(e.message || e)));
  }, []);

  // Section metadata that the API doesn't return: emoji icon + when-fires hint.
  // Kept here in the SPA so it's a single source of truth for visual cues.
  const SECTION_HINTS: Record<string, { icon: string; fires: string; kind?: string }> = {
    preflight:               { icon: '🛑', fires: 'always first',                                    kind: 'declarative' },
    customer:                { icon: '👤', fires: 'after preflight; both owner & landlord',          kind: 'compute-delegate' },
    tenant:                  { icon: '🏠', fires: 'rental only, after landlord captured',            kind: 'compute-delegate' },
    customer_picker:         { icon: '🔍', fires: 'owner-occupied only, before customer captured',  kind: 'compute-delegate' },
    start_choice:            { icon: '▶️', fires: 'after customer/tenant',                          kind: 'declarative' },
    tightness:               { icon: '🧪', fires: 'if engineer picked tightness',                   kind: 'compute-delegate' },
    appliances:              { icon: '🔥', fires: 'if engineer picked appliances, or after tightness done', kind: 'compute-delegate' },
    cert_safety:             { icon: '🛡', fires: 'after appliance walk done',                       kind: 'declarative' },
    installation_pipework:   { icon: '🔧', fires: 'after cert_safety done',                          kind: 'declarative' },
    defects:                 { icon: '⚠️', fires: 'after Section I done',                            kind: 'declarative' },
    signoff:                 { icon: '✍️', fires: 'after full_installation_safe captured',          kind: 'declarative' },
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-1">
        <Link to="/voice-dialog" className="text-sm text-orange-700 hover:underline">← Sections</Link>
      </div>
      <h1 className="text-2xl font-semibold mb-1">Cert Flow Visualiser</h1>
      <p className="text-sm text-gray-600 mb-5">
        Top-down map of how a Smart Gas Safety cert progresses through Alice's conversation.
        Click any section to open its editor.
      </p>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded p-3 text-sm mb-4">{error}</div>
      )}
      {!items && !error && (
        <div className="text-sm text-gray-500">Loading flow…</div>
      )}

      {items && (
        <div className="relative">
          {/* Start node */}
          <div className="text-center mb-2">
            <div className="inline-block px-4 py-2 bg-gray-800 text-white rounded-full text-xs font-mono font-semibold">▶ START</div>
          </div>

          {items.map((s, i) => {
            const hint = SECTION_HINTS[s.id] || { icon: '📋', fires: '—', kind: 'unknown' };
            const isCompute = hint.kind === 'compute-delegate';
            const sectionColor = isCompute
              ? 'bg-amber-50 border-amber-300 hover:border-amber-500'
              : 'bg-emerald-50 border-emerald-300 hover:border-emerald-500';
            return (
              <div key={s.id}>
                {/* Arrow connector (not on first) */}
                {i > 0 && (
                  <div className="flex justify-center my-1">
                    <div className="w-0 h-6 border-l-2 border-gray-300"></div>
                  </div>
                )}
                {/* Section card */}
                <Link to={`/voice-dialog/${s.id}`}
                      className={`block border-2 rounded-lg p-4 transition shadow-sm hover:shadow-md ${sectionColor}`}>
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{hint.icon}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-gray-700">order {s.order}</span>
                        <span className="font-semibold text-gray-900">{s.label}</span>
                        <span className="font-mono text-xs text-gray-500">({s.id})</span>
                      </div>
                      <div className="text-xs text-gray-600 mt-1">
                        <span className="italic">{hint.fires}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-1.5 justify-end mb-1">
                        {s.locked ? (
                          <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-800 border border-green-300 rounded">🔒</span>
                        ) : (
                          <span className="text-xs px-1.5 py-0.5 bg-amber-100 text-amber-800 border border-amber-300 rounded">🔓</span>
                        )}
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                          isCompute ? 'bg-amber-200 text-amber-900' : 'bg-emerald-200 text-emerald-900'
                        }`}>
                          {hint.kind === 'compute-delegate' ? 'compute' : 'declarative'}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500">{s.node_count} node{s.node_count === 1 ? '' : 's'}</div>
                    </div>
                  </div>
                </Link>
              </div>
            );
          })}

          {/* End node */}
          <div className="flex justify-center my-1">
            <div className="w-0 h-6 border-l-2 border-gray-300"></div>
          </div>
          <div className="text-center">
            <div className="inline-block px-4 py-2 bg-gray-800 text-white rounded-full text-xs font-mono font-semibold">✅ DONE</div>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="mt-8 p-4 bg-white border border-gray-200 rounded-lg text-xs text-gray-700">
        <div className="font-semibold uppercase tracking-wide text-gray-500 mb-2">Legend</div>
        <div className="flex flex-wrap gap-4">
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-emerald-200 border border-emerald-400 rounded"></span> declarative — full node editing in admin</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-amber-200 border border-amber-400 rounded"></span> compute — handler delegates, wordings editable</span>
          <span className="flex items-center gap-1">🔒 locked</span>
          <span className="flex items-center gap-1">🔓 unlocked</span>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
//  EDITOR PAGE
// ════════════════════════════════════════════════════════════════════════
export function VoiceDialogEditorPage() {
  const { section } = useParams<{ section: string }>();
  
  const toast = useToast();
  const [detail, setDetail] = useState<SectionDetail | null>(null);
  const [originalRaw, setOriginalRaw] = useState<string>('');
  const [draftRaw, setDraftRaw] = useState<string>('');
  const [draftParsed, setDraftParsed] = useState<SectionConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saveErrors, setSaveErrors] = useState<string[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [rawMode, setRawMode] = useState(false);
  const [activeTab, setActiveTab] = useState<'nodes'|'wordings'|'history'|'test'|'preview'>('nodes');
  const [testMsg, setTestMsg] = useState('');
  const [testControl, setTestControl] = useState<'start'|'continue'>('start');
  const [testSnapJson, setTestSnapJson] = useState('{}');
  const [testResp, setTestResp] = useState<TestResponse | null>(null);
  const [testRunning, setTestRunning] = useState(false);
  const [testError, setTestError] = useState<string|null>(null);
  const [wordings, setWordings] = useState<WordingsData|null>(null);
  const [history, setHistory] = useState<HistoryItem[]|null>(null);
  const [wordingsDraft, setWordingsDraft] = useState<Record<string,string>>({});
  const [wordingsSaving, setWordingsSaving] = useState<Record<string,boolean>>({});
  const [togglingLock, setTogglingLock] = useState(false);
  const [certSchema, setCertSchema] = useState<CertSchema|null>(null);
  const [pdfView, setPdfView] = useState<'engineer'|'customer'>('engineer');
  const [focusedNodeId, setFocusedNodeId] = useState<string|null>(null);
  const [editingSample, setEditingSample] = useState(false);
  const [pdfRendering, setPdfRendering] = useState(false);
  const [pdfDocType, setPdfDocType] = useState('smart_gas_safety');
  const [pdfLiveUrl, setPdfLiveUrl] = useState<string|null>(null);
  const [pdfLiveErr, setPdfLiveErr] = useState<string|null>(null);
  const [sampleOverrides, setSampleOverrides] = useState<Record<string,string>>(() => {
    try { return JSON.parse(localStorage.getItem('ot_voice_sample_overrides') || '{}'); }
    catch { return {}; }
  });

  // ── Load section ──
  const reload = useCallback(() => {
    if (!section) return;
    setError(null);
    api<SectionDetail>(`/admin/voice-dialog/sections/${section}`)
      .then(d => {
        setDetail(d);
        setOriginalRaw(d.raw);
        setDraftRaw(d.raw);
        setDraftParsed(d.parsed);
      })
      .catch(e => setError(String(e.message || e)));
  }, [section]);
  useEffect(reload, [reload]);

  const dirty = draftRaw !== originalRaw;

  // ── Re-parse the draft whenever raw or structured edits happen ──
  const reparse = useCallback((newRaw: string) => {
    setDraftRaw(newRaw);
    // Best-effort parse; failures don't break the editor, they just disable Save
    try {
      const stripped = stripJsonc(newRaw);
      const parsed = JSON.parse(stripped) as SectionConfig;
      setDraftParsed(parsed);
      setSaveErrors(null);
    } catch (_e) { /* parse error — Save will show it */ }
  }, []);

  // ── Patch a single field in the parsed view ──
  const patchNode = useCallback((nodeId: string, patch: Partial<SectionNode>) => {
    if (!draftParsed) return;
    const next: SectionConfig = JSON.parse(JSON.stringify(draftParsed));
    next.nodes[nodeId] = { ...next.nodes[nodeId], ...patch } as SectionNode;
    setDraftParsed(next);
    // Re-serialize to raw, preserving the original header comments if possible
    const newRaw = stringifyConfig(next, originalRaw);
    setDraftRaw(newRaw);
  }, [draftParsed, originalRaw]);

  const patchNodePrompt = useCallback((nodeId: string, key: keyof NodePrompt, value: string) => {
    if (!draftParsed) return;
    const node = draftParsed.nodes[nodeId];
    const prompt = { ...(node.prompt || {}), [key]: value };
    patchNode(nodeId, { prompt });
  }, [draftParsed, patchNode]);

  const patchMatcherPattern = useCallback((nodeId: string, mIdx: number, pattern: string) => {
    if (!draftParsed) return;
    const node = draftParsed.nodes[nodeId];
    if (!Array.isArray(node.matchers)) return;
    const matchers = node.matchers.map((m, i) => i === mIdx ? { ...m, pattern } : m);
    patchNode(nodeId, { matchers });
  }, [draftParsed, patchNode]);

  const patchMatcherAck = useCallback((nodeId: string, mIdx: number, ack: string) => {
    if (!draftParsed) return;
    const node = draftParsed.nodes[nodeId];
    if (!Array.isArray(node.matchers)) return;
    const matchers = node.matchers.map((m, i) => i === mIdx ? { ...m, ack } : m);
    patchNode(nodeId, { matchers });
  }, [draftParsed, patchNode]);

  // ── Save ──
  const save = useCallback(async () => {
    if (!section || !dirty) return;
    setSaving(true);
    setSaveErrors(null);
    try {
      const r = await api<{ ok: boolean }>(`/admin/voice-dialog/sections/${section}`, {
        method: 'PUT',
        body: JSON.stringify({ raw: draftRaw }),
      });
      if (r.ok) {
        toast.push(`Saved · ${section}`, 'success');
        setOriginalRaw(draftRaw);
        reload();
      }
    } catch (e: unknown) {
      const err = e as { errors?: string[]; message?: string };
      if (err.errors) setSaveErrors(err.errors);
      else setSaveErrors([err.message || 'save failed']);
    } finally { setSaving(false); }
  }, [section, dirty, draftRaw, toast, reload]);

  const discard = useCallback(() => {
    setDraftRaw(originalRaw);
    try {
      const parsed = JSON.parse(stripJsonc(originalRaw)) as SectionConfig;
      setDraftParsed(parsed);
    } catch (_e) { /* ignore */ }
    setSaveErrors(null);
  }, [originalRaw]);

  const download = useCallback(() => {
    if (!section) return;
    const blob = new Blob([draftRaw], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${section}.jsonc`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  }, [section, draftRaw]);

  // ── Wordings tab: load registry + overrides ──
  const loadWordings = useCallback(() => {
    api<WordingsApiResponse>('/admin/voice-sections/wordings')
      .then(d => { setWordings(d); setWordingsDraft({}); })
      .catch(e => console.warn('wordings load failed:', e));
  }, []);

  // ── History tab: load snapshots for this section ──
  const loadHistory = useCallback(() => {
    if (!section) return;
    api<HistoryApiResponse>(`/admin/voice-dialog/sections/${section}/history`)
      .then(d => setHistory(d.history || []))
      .catch(e => console.warn('history load failed:', e));
  }, [section]);

  // Lazy-load when tab opened
  useEffect(() => {
    if (activeTab === 'wordings' && !wordings) loadWordings();
    if (activeTab === 'history' && !history) loadHistory();
  }, [activeTab, wordings, history, loadWordings, loadHistory]);

  const saveWording = useCallback(async (id: string, text: string) => {
    setWordingsSaving(s => ({ ...s, [id]: true }));
    try {
      await api(`/admin/voice-sections/wordings`, {
        method: 'PUT',
        body: JSON.stringify({ id, text }),
      });
      toast.push(`Saved · ${id}`, 'success');
      setWordingsDraft(d => { const n = { ...d }; delete n[id]; return n; });
      loadWordings();
    } catch (e: unknown) {
      const err = e as { error?: string; message?: string };
      toast.push(err.error || err.message || 'wording save failed', 'error');
    } finally {
      setWordingsSaving(s => { const n = { ...s }; delete n[id]; return n; });
    }
  }, [toast, loadWordings]);

  const resetWording = useCallback(async (id: string) => {
    setWordingsSaving(s => ({ ...s, [id]: true }));
    try {
      await api(`/admin/voice-sections/wordings`, {
        method: 'PUT',
        body: JSON.stringify({ id, text: '' }),
      });
      toast.push(`Reset · ${id}`, 'success');
      setWordingsDraft(d => { const n = { ...d }; delete n[id]; return n; });
      loadWordings();
    } catch (e: unknown) {
      const err = e as { error?: string; message?: string };
      toast.push(err.error || err.message || 'wording reset failed', 'error');
    } finally {
      setWordingsSaving(s => { const n = { ...s }; delete n[id]; return n; });
    }
  }, [toast, loadWordings]);

  const rollback = useCallback(async (snapshotTs: string) => {
    if (!section) return;
    if (!confirm(`Restore the snapshot from ${snapshotTs}? Current ${section} content will be archived to history before being replaced.`)) return;
    try {
      await api(`/admin/voice-dialog/sections/${section}/rollback`, {
        method: 'POST',
        body: JSON.stringify({ snapshot_ts: snapshotTs }),
      });
      toast.push(`Restored · ${section} @ ${snapshotTs}`, 'success');
      reload();
      loadHistory();
    } catch (e: unknown) {
      const err = e as { error?: string; message?: string };
      toast.push(err.error || err.message || 'rollback failed', 'error');
    }
  }, [section, toast, reload, loadHistory]);

  // ── Test Runner: scenario presets per section ──
  const scenarios: Record<string, TestScenario[]> = {
    preflight: [
      { label: 'First ask (start)',           snap: {},                                                                              msg: '',           control: 'start' },
      { label: 'Q1 Yes → Q2 fuel',            snap: {},                                                                              msg: 'Yes',        control: 'continue' },
      { label: 'Q1 abort (smell)',            snap: {},                                                                              msg: 'I smell gas',control: 'continue' },
      { label: 'Q2 Natural gas → Q3',         snap: { _preflight_safety_ok: 'Yes' },                                                  msg: 'natural gas',control: 'continue' },
      { label: 'Q3 Owned',                    snap: { _preflight_safety_ok: 'Yes', _installation_fuel: 'NG' },                       msg: 'owned',      control: 'continue' },
    ],
    start_choice: [
      { label: 'First ask',                   snap: { _preflight_safety_ok: 'Yes', _installation_property_type: 'owner-occupied', _customer_picked: 'Yes' }, msg: '', control: 'start' },
      { label: '"tightness test"',            snap: { _preflight_safety_ok: 'Yes', _installation_property_type: 'owner-occupied', _customer_picked: 'Yes' }, msg: 'tightness test', control: 'continue' },
      { label: '"appliances"',                snap: { _preflight_safety_ok: 'Yes', _installation_property_type: 'owner-occupied', _customer_picked: 'Yes' }, msg: 'appliances', control: 'continue' },
    ],
    cert_safety: [
      { label: 'First ask (smoke alarm)',     snap: { _appliance_walk_done: 'Yes' },                                                  msg: '',     control: 'start' },
      { label: 'Yes',                         snap: { _appliance_walk_done: 'Yes' },                                                  msg: 'Yes',  control: 'continue' },
      { label: 'No',                          snap: { _appliance_walk_done: 'Yes' },                                                  msg: 'No',   control: 'continue' },
      { label: 'Unintelligible (retry)',      snap: { _appliance_walk_done: 'Yes' },                                                  msg: 'maybe',control: 'continue' },
    ],
    installation_pipework: [
      { label: 'Q1 first ask',                snap: { _cert_safety_done: 'Yes' },                                                     msg: '',     control: 'start' },
      { label: 'Q1 Pass → Q2',                snap: { _cert_safety_done: 'Yes' },                                                     msg: 'Pass', control: 'continue' },
      { label: 'Q1 "Pause" → captured as Pass', snap: { _cert_safety_done: 'Yes' },                                                  msg: 'Pause',control: 'continue' },
      { label: 'Q6 last (all Pass)',          snap: { _cert_safety_done: 'Yes', pipework_visual: 'Pass', supply_visual: 'Pass', ecv_access: 'Pass', equipotential_bonding: 'Pass', gas_installation_labelled: 'Pass' }, msg: 'Pass', control: 'continue' },
    ],
    defects: [
      { label: 'First ask',                   snap: { _section_i_done: 'Yes' },                                                       msg: '',                                       control: 'start' },
      { label: 'No defects → sign-off',       snap: { _section_i_done: 'Yes' },                                                       msg: 'No',                                     control: 'continue' },
      { label: 'Q4 risk class At-Risk',       snap: { _section_i_done: 'Yes', _defects_asked: 'Yes', defect_details: 'x', remedial_action: 'y' }, msg: 'at-risk',                  control: 'continue' },
    ],
    signoff: [
      { label: 'First ask',                   snap: { full_installation_safe: 'Yes' },                                                msg: '',     control: 'start' },
      { label: 'Yes → DONE',                  snap: { full_installation_safe: 'Yes', _signoff_prompted: 'Yes' },                     msg: 'Yes',  control: 'continue' },
      { label: 'No → PAUSED',                 snap: { full_installation_safe: 'Yes', _signoff_prompted: 'Yes' },                     msg: 'No',   control: 'continue' },
    ],
    customer: [
      { label: 'Owner-occupied first ask',    snap: { _installation_property_type: 'owner-occupied', property_type: 'owner-occupied' }, msg: '',  control: 'start' },
      { label: 'Landlord first ask',          snap: { _installation_property_type: 'landlord',       property_type: 'landlord' },       msg: '',  control: 'start' },
    ],
    tenant: [
      { label: 'First ask (rental, landlord done)', snap: { _installation_property_type: 'landlord', property_type: 'landlord', _landlord_captured: 'Yes' }, msg: '', control: 'start' },
    ],
    customer_picker: [
      { label: 'Initial greeting',            snap: {},                                                                              msg: '',          control: 'start' },
      { label: 'Search "Paul Lambert"',       snap: {},                                                                              msg: 'Paul Lambert', control: 'continue' },
    ],
    tightness: [
      { label: 'Q1 gauge first ask',          snap: { _start_choice: 'tightness', _installation_fuel: 'NG' },                        msg: '',         control: 'start' },
      { label: 'Q1 "Digital"',                snap: { _start_choice: 'tightness', _installation_fuel: 'NG' },                        msg: 'Digital',  control: 'continue' },
    ],
    appliances: [
      { label: 'First appliance ask',         snap: { _start_choice: 'appliances', _appliances_confirmed: true, appliances: [] }, msg: '', control: 'start' },
      { label: '"Boiler in the kitchen"',     snap: { _start_choice: 'appliances', _appliances_confirmed: true, appliances: [] }, msg: 'boiler in the kitchen', control: 'continue' },
    ],
  };

  const loadScenario = useCallback((sc: TestScenario) => {
    setTestSnapJson(JSON.stringify(sc.snap, null, 2));
    setTestMsg(sc.msg || '');
    setTestControl(sc.control || 'continue');
    setTestResp(null);
    setTestError(null);
  }, []);

  const runTest = useCallback(async () => {
    if (!section) return;
    setTestRunning(true);
    setTestError(null);
    setTestResp(null);
    let snap: Record<string, unknown> = {};
    try {
      snap = JSON.parse(testSnapJson || '{}');
      if (!snap || typeof snap !== 'object' || Array.isArray(snap)) throw new Error('snapshot must be a JSON object');
    } catch (e) {
      setTestError('Invalid snapshot JSON: ' + (e as Error).message);
      setTestRunning(false);
      return;
    }
    try {
      const r = await api<TestResponse>(`/admin/voice-dialog/sections/${section}/test`, {
        method: 'POST',
        body: JSON.stringify({ last_user_message: testMsg, form_snapshot: snap, control: testControl }),
      });
      setTestResp(r);
    } catch (e: unknown) {
      const err = e as { error?: string; message?: string };
      setTestError(err.error || err.message || 'test failed');
    } finally {
      setTestRunning(false);
    }
  }, [section, testMsg, testSnapJson, testControl]);

  const toggleLock = useCallback(async () => {
    if (!section || !draftParsed) return;
    const currentlyLocked = !!draftParsed.locked;
    if (currentlyLocked && !confirm(`Unlock section "${section}"? Wording overrides will then be accepted. You can re-lock from this same button when you're done.`)) return;
    setTogglingLock(true);
    try {
      await api(`/admin/voice-sections/locks`, {
        method: 'POST',
        body: JSON.stringify({ section_id: section, locked: !currentlyLocked }),
      });
      toast.push(currentlyLocked ? `Unlocked · ${section}` : `Locked · ${section}`, 'success');
      reload();
    } catch (e: unknown) {
      const err = e as { error?: string; message?: string };
      toast.push(err.error || err.message || 'lock toggle failed', 'error');
    } finally {
      setTogglingLock(false);
    }
  }, [section, draftParsed, toast, reload]);

  // ── Cert Preview: lazy-load smart_gas_safety schema ──
  const loadSchema = useCallback(() => {
    api<CertSchema>('/admin/schemas/certificates/smart_gas_safety')
      .then(d => setCertSchema(d))
      .catch(e => console.warn('schema load failed:', e));
  }, []);

  useEffect(() => {
    if (activeTab === 'preview' && !certSchema) loadSchema();
  }, [activeTab, certSchema, loadSchema]);

  // Compute the set of cert-field IDs this section captures. Used to
  // highlight those fields on the cert preview.
  const capturedFieldIds = useMemo(() => {
    if (!draftParsed) return new Set<string>();
    const out = new Set<string>();
    for (const nid of Object.keys(draftParsed.nodes || {})) {
      const n = draftParsed.nodes[nid];
      if (n && n.captures && n.captures.field) out.add(n.captures.field);
      if (n && n.free_text && n.free_text.field) out.add(n.free_text.field);
      if (n && Array.isArray(n.matchers)) {
        for (const m of (n.matchers as NodeMatcher[])) {
          if (m && Array.isArray(m.actions)) {
            for (const a of m.actions) {
              const ax = a as { type?: string; field_id?: string };
              if (ax && ax.type === 'update_field' && ax.field_id) out.add(ax.field_id);
              if (ax && ax.type === 'update_appliance_field' && ax.field_id) out.add(ax.field_id);
            }
          }
        }
      }
    }
    return out;
  }, [draftParsed]);

  // ── Focus on a single node: subset of capturedFieldIds for THAT node only ──
  const focusedFieldIds = useMemo(() => {
    if (!focusedNodeId || !draftParsed) return null;
    const node = draftParsed.nodes[focusedNodeId];
    if (!node) return null;
    const out = new Set<string>();
    if (node.captures && node.captures.field) out.add(node.captures.field);
    if (node.free_text && node.free_text.field) out.add(node.free_text.field);
    if (Array.isArray(node.matchers)) {
      for (const m of (node.matchers as NodeMatcher[])) {
        if (m && Array.isArray(m.actions)) {
          for (const a of m.actions) {
            const ax = a as { type?: string; field_id?: string };
            if (ax && (ax.type === 'update_field' || ax.type === 'update_appliance_field') && ax.field_id) {
              out.add(ax.field_id);
            }
          }
        }
      }
    }
    return out;
  }, [focusedNodeId, draftParsed]);

  // ── Editable sample data helpers ──
  const getEffectiveSampleValue = useCallback((fieldId: string, defaultSrc: Record<string,string>): string => {
    if (sampleOverrides[fieldId] !== undefined) return sampleOverrides[fieldId];
    return defaultSrc[fieldId] !== undefined ? defaultSrc[fieldId] : '';
  }, [sampleOverrides]);

  const setSampleValue = useCallback((fieldId: string, value: string) => {
    const next = { ...sampleOverrides };
    if (value === '' || value === undefined || value === null) delete next[fieldId];
    else next[fieldId] = value;
    setSampleOverrides(next);
    try { localStorage.setItem('ot_voice_sample_overrides', JSON.stringify(next)); } catch {}
  }, [sampleOverrides]);

  const resetSampleData = useCallback(() => {
    if (!confirm('Reset all sample data overrides to the built-in defaults?')) return;
    setSampleOverrides({});
    try { localStorage.removeItem('ot_voice_sample_overrides'); } catch {}
  }, []);

  // ── Render the real cert PDF from current sample data ──
  const renderRealPdf = useCallback(async () => {
    setPdfRendering(true);
    try {
      const certData: Record<string,string> = {};
      for (const k of Object.keys(SAMPLE_CERT_DATA)) {
        certData[k] = sampleOverrides[k] !== undefined ? sampleOverrides[k] : SAMPLE_CERT_DATA[k];
      }
      const appliance: Record<string,string> = {};
      for (const k of Object.keys(SAMPLE_APPLIANCE)) {
        appliance[k] = sampleOverrides[k] !== undefined ? sampleOverrides[k] : SAMPLE_APPLIANCE[k];
      }
      // Shared previewDoc helper — posts to the doc-preview endpoint (backed
      // by lib/doc-renderer) and opens the returned PDF in a new tab.
      await previewDoc({ doc_type: pdfDocType, cert_data: certData, appliances: [appliance], audience: pdfView });
      toast.push('PDF rendered — opened in new tab', 'success');
    } catch (e) {
      toast.push('PDF render failed: ' + (e as Error).message, 'error');
    } finally {
      setPdfRendering(false);
    }
  }, [sampleOverrides, pdfView, pdfDocType, toast]);

  // ── Live PDF preview ──
  // Renders the real cert PDF beside the editor and re-renders (debounced)
  // whenever the sample data, view, or doc type changes — so edits show up
  // almost in real time. Returns a blob URL embedded in the right-hand panel.
  const renderLivePdf = useCallback(async () => {
    setPdfRendering(true);
    setPdfLiveErr(null);
    try {
      const certData: Record<string,string> = {};
      for (const k of Object.keys(SAMPLE_CERT_DATA)) {
        certData[k] = sampleOverrides[k] !== undefined ? sampleOverrides[k] : SAMPLE_CERT_DATA[k];
      }
      const appliance: Record<string,string> = {};
      for (const k of Object.keys(SAMPLE_APPLIANCE)) {
        appliance[k] = sampleOverrides[k] !== undefined ? sampleOverrides[k] : SAMPLE_APPLIANCE[k];
      }
      const url = await renderDocBlobUrl({ doc_type: pdfDocType, cert_data: certData, appliances: [appliance], audience: pdfView });
      setPdfLiveUrl(prev => { if (prev) { try { URL.revokeObjectURL(prev); } catch { /* noop */ } } return url; });
    } catch (e) {
      setPdfLiveErr((e as Error).message);
    } finally {
      setPdfRendering(false);
    }
  }, [sampleOverrides, pdfView, pdfDocType]);

  // Debounced auto-render: 700ms after the last change, while the Cert
  // Preview tab is open. renderLivePdf's identity changes when the sample
  // data / view / doc type change, so this re-fires on every such change.
  useEffect(() => {
    if (activeTab !== 'preview') return;
    const t = setTimeout(() => { void renderLivePdf(); }, 700);
    return () => clearTimeout(t);
  }, [activeTab, renderLivePdf]);

  // ── Render ──
  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-800 rounded p-3 text-sm">
          {error}
        </div>
        <Link to="/voice-dialog" className="text-sm text-orange-700 hover:underline mt-3 inline-block">← Back to sections</Link>
      </div>
    );
  }
  if (!detail || !draftParsed) {
    return <div className="p-6 text-sm text-gray-500">Loading {section}…</div>;
  }

  const cfg = draftParsed;
  const nodeIds = Object.keys(cfg.nodes);

  return (
    <div className="flex h-full">
      {/* ── Sticky Save banner ── */}
      {dirty && (
        <div className="fixed top-0 left-0 right-0 z-30 bg-amber-100 border-b border-amber-300 px-6 py-2 flex items-center gap-4 text-sm shadow-sm">
          <span className="font-medium text-amber-900">Unsaved changes to <code className="font-mono">{section}.jsonc</code></span>
          <div className="ml-auto flex gap-2">
            <button onClick={discard} className="px-3 py-1.5 text-sm border border-gray-300 bg-white rounded hover:bg-gray-50">Discard</button>
            <button
              onClick={save}
              disabled={saving}
              className="px-4 py-1.5 text-sm bg-orange-600 hover:bg-orange-700 text-white rounded font-medium disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {/* ── Main editor column ── */}
      <div className={`flex-1 overflow-y-auto p-6 ${activeTab === 'preview' ? 'max-w-[1700px]' : 'max-w-5xl'} ${dirty ? 'pt-14' : ''}`}>
        {/* Crumbs */}
        <div className="text-xs text-gray-500 mb-2">
          <Link to="/voice-dialog" className="hover:text-orange-700 hover:underline">smart_gas_safety</Link>
          {' › '}
          <span className="text-gray-800 font-semibold">{section}</span>
        </div>

        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <h1 className="text-2xl font-semibold">{cfg.label}</h1>
          <button onClick={toggleLock} disabled={togglingLock}
                  title="Click to toggle section lock"
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border transition cursor-pointer hover:opacity-80 disabled:opacity-50 ${
                    cfg.locked
                      ? 'bg-green-50 text-green-800 border-green-300'
                      : 'bg-amber-50 text-amber-800 border-amber-300'
                  }`}>
            {togglingLock ? '…' : (cfg.locked ? '🔒' : '🔓')} {cfg.locked ? 'Locked' : 'Unlocked'} · {cfg.version || '—'} {cfg.verified && `· verified ${cfg.verified}`}
          </button>
        </div>
        <div className="text-sm text-gray-600 mb-5">
          <span className="font-mono">{section}</span> · {nodeIds.length} node{nodeIds.length === 1 ? '' : 's'} · order {cfg.order}
        </div>

        <div className="bg-green-50 border-l-4 border-green-500 text-green-900 rounded p-3 text-sm mb-4">
          <strong>All fields are editable.</strong> The lock pill is a soft warning — every input is live.
          Make any edit and the yellow Save banner appears at the top.
        </div>

        {/* Tab strip */}
        <div className="flex gap-0 border-b border-gray-200 mb-4">
          {(['nodes','wordings','history','test','preview'] as const).map(t => (
            <button key={t} onClick={() => setActiveTab(t)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
                activeTab === t
                  ? 'text-orange-700 border-orange-600'
                  : 'text-gray-600 border-transparent hover:text-gray-900'
              }`}>
              {t === 'nodes' ? `📋 Nodes (${nodeIds.length})`
                : t === 'wordings' ? '✏️ Wordings'
                : t === 'history'  ? '📜 History'
                : t === 'test'     ? '🧪 Test Runner'
                : '📄 Cert Preview'}
            </button>
          ))}
        </div>

        {/* Toolbar — only shown on Nodes tab */}
        {activeTab === 'nodes' && (
        <div className="flex flex-wrap gap-2 mb-5">
          <button onClick={download}
                  className="px-3 py-1.5 text-sm border border-gray-300 bg-white rounded hover:bg-gray-50">
            📥 Download JSON
          </button>
          <button onClick={() => setRawMode(!rawMode)}
                  className="px-3 py-1.5 text-sm border border-gray-300 bg-white rounded hover:bg-gray-50">
            {rawMode ? '← Structured editor' : 'Raw JSONC view'}
          </button>
        </div>
        )}

        {activeTab === 'nodes' && (
        <>
        {/* Save errors (server-side validation) */}
        {saveErrors && (
          <div className="bg-red-50 border-l-4 border-red-500 text-red-900 rounded p-3 text-sm mb-4">
            <strong>Save failed — validation errors:</strong>
            <ul className="list-disc pl-5 mt-1">
              {saveErrors.map((e, i) => <li key={i} className="font-mono text-xs">{e}</li>)}
            </ul>
          </div>
        )}

        {/* Raw mode */}
        {rawMode && (
          <div className="mb-4">
            <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1">Raw .jsonc</div>
            <textarea
              value={draftRaw}
              onChange={e => reparse(e.target.value)}
              spellCheck={false}
              className="w-full h-[600px] font-mono text-xs bg-gray-900 text-gray-100 p-3 rounded border border-gray-700"
            />
          </div>
        )}

        {/* Structured mode: one card per node */}
        {!rawMode && nodeIds.map(nid => {
          const node = cfg.nodes[nid];
          return (
            <div key={nid} className="bg-white border border-gray-200 rounded-lg mb-4 overflow-hidden shadow-sm">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center gap-2">
                <span className="font-mono text-orange-700 font-semibold text-sm">{section}.{nid}</span>
                <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                  node.type === 'ask' ? 'bg-blue-100 text-blue-800' :
                  node.type === 'say' ? 'bg-green-100 text-green-800' :
                  node.type === 'goto' ? 'bg-amber-100 text-amber-800' :
                  'bg-gray-100 text-gray-800'
                }`}>{node.type}</span>
                <button
                  onClick={() => { setFocusedNodeId(nid); setActiveTab('preview'); }}
                  title="Focus this node on Cert Preview"
                  className="ml-auto text-xs px-2 py-0.5 rounded border border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100">
                  🎯 Preview
                </button>
              </div>
              <div className="p-4 space-y-3">

                {/* Prompt — default + retry */}
                {node.prompt && (
                  <>
                    <div>
                      <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1">
                        Question (default)
                        {node.prompt.editable_id && (
                          <span className="ml-2 text-gray-400 text-[10px] normal-case font-normal italic">
                            [editable_id: {node.prompt.editable_id}]
                          </span>
                        )}
                      </div>
                      <textarea
                        value={node.prompt.default || ''}
                        onChange={e => patchNodePrompt(nid, 'default', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                        rows={2}
                      />
                    </div>
                    {('retry' in node.prompt || node.prompt.retry !== undefined) && (
                      <div>
                        <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1">
                          Retry / "didn't catch"
                        </div>
                        <input
                          type="text"
                          value={node.prompt.retry || ''}
                          onChange={e => patchNodePrompt(nid, 'retry', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                      </div>
                    )}
                  </>
                )}

                {/* Captures field */}
                {node.captures && node.captures.field && (
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1">Captures field</div>
                      <input readOnly value={node.captures.field} className="w-full px-3 py-2 border border-gray-200 rounded text-sm bg-gray-50 font-mono text-gray-700" />
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1">Field type</div>
                      <input readOnly value={node.captures.type || ''} className="w-full px-3 py-2 border border-gray-200 rounded text-sm bg-gray-50 font-mono text-gray-700" />
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1">Next state</div>
                      <input readOnly value={node.next_state || 'AWAITING_ANSWER'} className="w-full px-3 py-2 border border-gray-200 rounded text-sm bg-gray-50 font-mono text-gray-700" />
                    </div>
                  </div>
                )}

                {/* Matchers (inline regex list) */}
                {Array.isArray(node.matchers) && node.matchers.length > 0 && (
                  <div>
                    <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-2">
                      Matchers <span className="text-gray-400 normal-case font-normal italic">— first match wins · regex applied with /i</span>
                    </div>
                    {node.matchers.map((m, idx) => (
                      <div key={idx} className="border border-gray-200 rounded p-3 mb-2 bg-gray-50">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2.5 py-0.5 bg-green-100 text-green-800 rounded text-xs font-mono font-semibold">
                            "{m.value || '?'}"
                          </span>
                          <span className="text-gray-400">→</span>
                          <input
                            type="text"
                            value={m.pattern}
                            onChange={e => patchMatcherPattern(nid, idx, e.target.value)}
                            className="flex-1 px-2 py-1 text-xs font-mono border border-gray-300 rounded"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Ack</span>
                          <input
                            type="text"
                            value={m.ack || ''}
                            onChange={e => patchMatcherAck(nid, idx, e.target.value)}
                            className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
                          />
                        </div>
                        {m.actions && m.actions.length > 0 && (
                          <div className="text-xs text-gray-500 mt-2">
                            Actions: <span className="font-mono">{m.actions.map(a => a.type).join(', ')}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Free-text node */}
                {node.free_text && (
                  <div className="border border-gray-200 rounded p-3 bg-gray-50">
                    <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1">Free text capture</div>
                    <div className="text-sm">
                      Captures <span className="font-mono text-orange-700">{node.free_text.field}</span> (max {node.free_text.max || 500} chars)
                    </div>
                    <div className="text-xs text-gray-500 mt-1">Ack: "{node.free_text.ack}"</div>
                  </div>
                )}

                {/* Goto / say nodes */}
                {node.type === 'goto' && node.next && (
                  <div className="text-sm text-gray-700">
                    → goes to <span className="font-mono text-orange-700">{node.next}</span>
                  </div>
                )}
                {node.type === 'say' && node.text && (
                  <div>
                    <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1">Announcement text</div>
                    <input readOnly value={node.text} className="w-full px-3 py-2 border border-gray-200 rounded text-sm bg-gray-50" />
                  </div>
                )}

              </div>
            </div>
          );
        })}
        </>
        )}

        {/* Wordings tab */}
        {activeTab === 'wordings' && (
          <div>
            {!wordings && <div className="text-sm text-gray-500">Loading wordings…</div>}
            {wordings && (() => {
              const sectionPrefix = section + '.';
              const matchingIds = Object.keys(wordings.registry).filter(id => id.startsWith(sectionPrefix));
              return (
                <>
                  {cfg.locked ? (
                    <div className="bg-amber-50 border-l-4 border-amber-500 text-amber-900 rounded p-3 text-sm mb-4 flex items-center gap-3">
                      <span>🔒 <strong>Section is locked</strong> — saves below will be refused. Click the lock pill in the header (or below) to unlock.</span>
                      <button onClick={toggleLock} disabled={togglingLock}
                              className="ml-auto px-3 py-1.5 text-xs bg-white border border-amber-500 text-amber-900 rounded hover:bg-amber-100 disabled:opacity-50">
                        {togglingLock ? '…' : '🔓 Unlock section'}
                      </button>
                    </div>
                  ) : (
                    <div className="bg-blue-50 border-l-4 border-blue-500 text-blue-900 rounded p-3 text-sm mb-4">
                      Editable prompts registered for this section. Edits go to <code>/opt/onlytrade/data/voice-section-wordings.json</code> and are picked up by Alice on her next turn — no service restart needed.
                    </div>
                  )}
                  {matchingIds.length === 0 && (
                    <div className="text-sm text-gray-500 italic">No editable prompt IDs registered for this section yet. The handler text is hardcoded — open a ticket to expose any prompts you want to be able to edit.</div>
                  )}
                  {matchingIds.map(id => {
                    const reg = wordings.registry[id];
                    const override = wordings.overrides[id] || '';
                    const draftVal = wordingsDraft[id] !== undefined ? wordingsDraft[id] : override;
                    const dirtyW = (wordingsDraft[id] !== undefined) && (wordingsDraft[id] !== override);
                    return (
                      <div key={id} className="bg-white border border-gray-200 rounded-lg p-4 mb-3 shadow-sm">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-mono text-xs text-orange-700 font-semibold">{id}</span>
                          {override && <span className="text-xs px-2 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">overridden</span>}
                          {!override && <span className="text-xs px-2 py-0.5 rounded bg-gray-50 text-gray-600 border border-gray-200">default</span>}
                          <span className="ml-auto text-xs text-gray-500">{reg.label}</span>
                        </div>
                        <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1">Default text</div>
                        <div className="text-sm text-gray-600 italic mb-3 bg-gray-50 px-3 py-2 rounded border border-gray-100">{reg.default}</div>
                        <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1">Override</div>
                        <textarea
                          value={draftVal}
                          placeholder={'(leave empty to use default)'}
                          onChange={e => setWordingsDraft(d => ({ ...d, [id]: e.target.value }))}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                        <div className="flex gap-2 mt-2">
                          <button disabled={!dirtyW || wordingsSaving[id]}
                                  onClick={() => saveWording(id, draftVal)}
                                  className="px-3 py-1.5 text-xs bg-orange-600 hover:bg-orange-700 text-white rounded font-medium disabled:opacity-40">
                            {wordingsSaving[id] ? 'Saving…' : 'Save override'}
                          </button>
                          {override && (
                            <button disabled={wordingsSaving[id]}
                                    onClick={() => resetWording(id)}
                                    className="px-3 py-1.5 text-xs bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-40">
                              ↺ Reset to default
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </>
              );
            })()}
          </div>
        )}

        {/* History tab */}
        {activeTab === 'history' && (
          <div>
            {!history && <div className="text-sm text-gray-500">Loading history…</div>}
            {history && history.length === 0 && (
              <div className="text-sm text-gray-500 italic">No snapshots yet for this section. The first snapshot is taken automatically the next time you save.</div>
            )}
            {history && history.length > 0 && (
              <>
                <div className="bg-amber-50 border-l-4 border-amber-500 text-amber-900 rounded p-3 text-sm mb-4">
                  Every save creates a snapshot. Rollback restores the section to that snapshot and archives the current state as a fresh snapshot — so rollback itself is reversible.
                </div>
                <table className="w-full bg-white border border-gray-200 rounded-lg overflow-hidden text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-4 py-2 text-xs uppercase tracking-wide font-semibold text-gray-600">Snapshot timestamp</th>
                      <th className="text-left px-4 py-2 text-xs uppercase tracking-wide font-semibold text-gray-600">Saved at</th>
                      <th className="text-right px-4 py-2 text-xs uppercase tracking-wide font-semibold text-gray-600">Size</th>
                      <th className="text-right px-4 py-2 text-xs uppercase tracking-wide font-semibold text-gray-600">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map(h => (
                      <tr key={h.filename} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-2 font-mono text-xs text-gray-700">{h.snapshot_ts}</td>
                        <td className="px-4 py-2 text-xs text-gray-600">{new Date(h.mtime).toLocaleString()}</td>
                        <td className="px-4 py-2 text-xs text-gray-600 text-right">{h.size}B</td>
                        <td className="px-4 py-2 text-right">
                          <button onClick={() => rollback(h.snapshot_ts)}
                                  className="px-3 py-1 text-xs bg-white border border-amber-400 text-amber-800 rounded hover:bg-amber-50">
                            ↺ Restore
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        )}

        {/* Test Runner tab */}
        {activeTab === 'test' && (
          <div>
            <div className="bg-purple-50 border-l-4 border-purple-500 text-purple-900 rounded p-3 text-sm mb-4">
              Replays a single turn through the engine for this section. Pick a scenario from the presets, edit the inputs if needed, hit Run. Engine cache is cleared on every run — saved <code>.jsonc</code> edits are picked up immediately.
            </div>

            {/* Scenario presets */}
            {section && scenarios[section] && scenarios[section].length > 0 && (
              <div className="mb-4">
                <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-2">Preset scenarios</div>
                <div className="flex flex-wrap gap-2">
                  {scenarios[section].map((sc, i) => (
                    <button key={i} onClick={() => loadScenario(sc)}
                            className="px-3 py-1.5 text-xs bg-white border border-purple-300 text-purple-800 rounded hover:bg-purple-50">
                      {sc.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4 mb-4">
              {/* Snapshot JSON */}
              <div>
                <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1">form_snapshot (JSON)</div>
                <textarea
                  value={testSnapJson}
                  onChange={e => setTestSnapJson(e.target.value)}
                  rows={10}
                  spellCheck={false}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-xs font-mono focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {/* Message + control */}
              <div className="space-y-3">
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1">last_user_message</div>
                  <textarea
                    value={testMsg}
                    onChange={e => setTestMsg(e.target.value)}
                    rows={3}
                    placeholder="(empty = first-ask / silence)"
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1">control</div>
                  <div className="flex gap-2">
                    {(['start','continue'] as const).map(c => (
                      <button key={c} onClick={() => setTestControl(c)}
                              className={`px-3 py-1.5 text-xs rounded border ${
                                testControl === c
                                  ? 'bg-purple-600 text-white border-purple-600'
                                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                              }`}>
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
                <button onClick={runTest} disabled={testRunning}
                        className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded font-medium disabled:opacity-50">
                  {testRunning ? 'Running…' : '▶ Run turn'}
                </button>
              </div>
            </div>

            {/* Result */}
            {testError && (
              <div className="bg-red-50 border-l-4 border-red-500 text-red-900 rounded p-3 text-sm mb-4">
                <strong>Test error:</strong> {testError}
              </div>
            )}

            {testResp && (
              <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-2">Engine response</div>
                {testResp.response === null && (
                  <div className="text-sm italic text-gray-600">Engine returned <code className="font-mono">null</code> — section is not active for this snapshot, or silence_mode is silent. The section runner would fall through to the next imperative handler.</div>
                )}
                {testResp.response && (
                  <>
                    {testResp.response.speech && (
                      <div className="mb-3">
                        <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1">🗣 Speech</div>
                        <div className="bg-green-50 border border-green-200 rounded p-3 text-sm text-green-900">{testResp.response.speech}</div>
                      </div>
                    )}
                    {testResp.response.actions && testResp.response.actions.length > 0 && (
                      <div className="mb-3">
                        <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1">⚡ Actions ({testResp.response.actions.length})</div>
                        <pre className="bg-gray-50 border border-gray-200 rounded p-3 text-xs font-mono overflow-x-auto">{JSON.stringify(testResp.response.actions, null, 2)}</pre>
                      </div>
                    )}
                    <div className="flex gap-3 text-xs text-gray-600">
                      <span>state: <span className="font-mono text-gray-900">{testResp.response.next_state}</span></span>
                      <span>finished: <span className="font-mono text-gray-900">{String(testResp.response.finished)}</span></span>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Cert Preview tab */}
        {activeTab === 'preview' && (
          <div>
            <div className="bg-purple-50 border-l-4 border-purple-500 text-purple-900 rounded p-3 text-sm mb-4">
              📄 Cert layout from <code>smart_gas_safety.schema.json</code>, populated with <strong>sample data</strong> for visual reference. Fields highlighted in <span className="bg-orange-100 border border-orange-400 px-1 rounded">orange</span> are captured by {focusedNodeId ? <strong>node <code className="font-mono">{focusedNodeId}</code></strong> : <strong>this section</strong>}. Amber <span className="text-amber-900">⚠ trigger</span> callouts show where a value would auto-spawn a child cert.
            </div>

            <div className="flex items-center gap-3 mb-4">
              <div className="flex gap-1 border border-gray-300 rounded overflow-hidden">
                <button onClick={() => setPdfView('engineer')}
                        className={`px-3 py-1.5 text-xs font-medium ${
                          pdfView === 'engineer' ? 'bg-orange-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}>
                  👷 Engineer view
                </button>
                <button onClick={() => setPdfView('customer')}
                        className={`px-3 py-1.5 text-xs font-medium border-l border-gray-300 ${
                          pdfView === 'customer' ? 'bg-orange-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}>
                  🧑 Customer view
                </button>
              </div>
              <span className="text-xs text-gray-500">
                {pdfView === 'engineer'
                  ? 'All fields visible — full cert as the engineer sees it'
                  : 'Customer-visible fields only — what gets emailed to the customer'}
              </span>
              <button onClick={() => setEditingSample(v => !v)}
                      className={`px-3 py-1.5 text-xs font-medium rounded border ${
                        editingSample
                          ? 'bg-orange-600 text-white border-orange-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}>
                {editingSample ? '✓ Done editing' : '✏️ Edit sample data'}
              </button>
              <select value={pdfDocType} onChange={e => setPdfDocType(e.target.value)}
                      title="Which certificate template the Render button outputs"
                      className="px-2 py-1.5 text-xs font-medium rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50">
                <option value="smart_gas_safety">Smart Gas Safety Record</option>
                <option value="gas_safety_cp12">Gas Safety Record (CP12)</option>
                <option value="warning_notice">Gas Warning / Advice Notice</option>
                <option value="service_report">Boiler / Appliance Service Report</option>
              </select>
              <button onClick={renderRealPdf} disabled={pdfRendering}
                      className="px-3 py-1.5 text-xs font-medium rounded border bg-red-600 text-white border-red-600 hover:bg-red-700 disabled:opacity-50">
                {pdfRendering ? '⏳ Rendering…' : '📄 Render real PDF'}
              </button>
              {Object.keys(sampleOverrides).length > 0 && (
                <button onClick={resetSampleData}
                        title={`${Object.keys(sampleOverrides).length} custom value(s) — reset to defaults`}
                        className="px-3 py-1.5 text-xs bg-amber-50 border border-amber-300 text-amber-800 rounded hover:bg-amber-100">
                  ↺ Reset ({Object.keys(sampleOverrides).length})
                </button>
              )}
              {focusedNodeId && (
                <button onClick={() => setFocusedNodeId(null)}
                        className="ml-auto px-3 py-1.5 text-xs bg-purple-100 text-purple-800 border border-purple-300 rounded hover:bg-purple-200">
                  ✕ Clear focus on {focusedNodeId}
                </button>
              )}
            </div>
            <div className="flex gap-4 items-start">
            <div className="flex-1 min-w-0">
            {!certSchema && <div className="text-sm text-gray-500">Loading schema…</div>}
            {certSchema && (
              <div className="bg-white border border-gray-300 rounded-lg overflow-hidden shadow-sm">
                {/* Cert header */}
                <div className="bg-gray-900 text-white px-5 py-3">
                  <div className="text-xs uppercase tracking-wide opacity-70">Gas Safety</div>
                  <div className="text-lg font-semibold">{certSchema.label}</div>
                </div>
                <div className="p-4 space-y-4">
                  {certSchema.sections.map((sec, _sidx) => {
                    const allFields = sec.fields || sec.fields_per_appliance || [];
                    const fields = pdfView === 'customer'
                      ? allFields.filter(f => f.customer_visible !== false)
                      : allFields;
                    if (!fields.length) return null;
                    const highlightSet = focusedFieldIds || capturedFieldIds;
                    const sectionHasCaptures = fields.some(f => highlightSet.has(f.id));
                    return (
                      <div key={sec.id} className={`border rounded-lg p-3 ${
                        sectionHasCaptures ? 'border-orange-300 bg-orange-50' : 'border-gray-200 bg-gray-50'
                      }`}>
                        <div className="flex items-center gap-2 mb-2">
                          {sec.letter && (
                            <span className="font-mono text-xs bg-gray-900 text-white px-2 py-0.5 rounded">{sec.letter}</span>
                          )}
                          <span className="font-semibold text-gray-900 text-sm">{sec.label}</span>
                          <span className="font-mono text-xs text-gray-500">
                            ({fields.length} field{fields.length === 1 ? '' : 's'}
                            {pdfView === 'customer' && allFields.length !== fields.length && ` · ${allFields.length - fields.length} hidden`})
                          </span>
                          {sec.type === 'appliance_list' && (
                            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-800 rounded">per-appliance</span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-1 text-xs">
                          {fields.map(f => {
                            const captured = highlightSet.has(f.id);
                            const sampleSrc = sec.type === 'appliance_list' ? SAMPLE_APPLIANCE : SAMPLE_CERT_DATA;
                            const sampleVal = getEffectiveSampleValue(f.id, sampleSrc);
                            const isOverridden = sampleOverrides[f.id] !== undefined;
                            const fieldTriggers = TRIGGER_RULES[f.id] || [];
                            const valLooksPositive = sampleVal === 'Pass' || sampleVal === 'Yes';
                            const valLooksNegative = sampleVal === 'Fail' || sampleVal === 'No' || sampleVal === 'NA';
                            return (
                              <div key={f.id}
                                   className={`px-2 py-1 rounded border ${
                                     captured
                                       ? 'bg-orange-100 border-orange-400 text-orange-900'
                                       : 'bg-white border-gray-200 text-gray-700'
                                   }`}>
                                <div className="flex items-center gap-1.5">
                                  <span className={`truncate ${captured ? 'font-semibold' : ''}`}>{f.label || f.id}</span>
                                  {f.required && <span className="text-red-600 text-[10px]">*</span>}
                                  {f.customer_visible === false && (
                                    <span className="text-[9px] text-gray-400" title="Not shown to customer on PDF">eng-only</span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 mt-0.5">
                                  <span className="font-mono text-[10px] text-gray-500">{f.id}</span>
                                  {editingSample ? (
                                    <input
                                      type="text"
                                      value={sampleVal}
                                      onChange={e => setSampleValue(f.id, e.target.value)}
                                      className={`ml-auto w-32 px-1.5 py-0.5 rounded text-[10px] border ${
                                        isOverridden ? 'border-orange-400 bg-orange-50' : 'border-gray-300 bg-white'
                                      }`}
                                    />
                                  ) : sampleVal && (
                                    <span className={`ml-auto px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                      valLooksPositive ? 'bg-green-100 text-green-800' :
                                      valLooksNegative ? 'bg-red-100 text-red-800' :
                                      'bg-blue-50 text-blue-800'
                                    } ${isOverridden ? 'ring-1 ring-orange-400' : ''}`}
                                          title={isOverridden ? 'Custom value — click Reset to clear' : undefined}>{sampleVal}</span>
                                  )}
                                </div>
                                {fieldTriggers.length > 0 && (
                                  <div className="mt-1 space-y-0.5">
                                    {fieldTriggers.map((tr, ti) => (
                                      <div key={ti}
                                           className={`text-[10px] flex items-start gap-1 px-1.5 py-0.5 rounded ${
                                             tr.severity === 'warning'
                                               ? 'bg-amber-50 border border-amber-300 text-amber-900'
                                               : 'bg-blue-50 border border-blue-200 text-blue-900'
                                           }`}
                                           title={tr.reason || ''}>
                                        <span className="font-semibold">⚠ if "{tr.whenValue}"</span>
                                        <span>→ {tr.spawns}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            </div>

            {/* ── Live real PDF — re-renders as the sample data changes ── */}
            <div className="w-[620px] shrink-0">
              <div className="sticky top-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm font-semibold text-gray-800">📄 Live PDF</span>
                  {pdfRendering && <span className="text-xs text-orange-600">⏳ rendering…</span>}
                  {pdfLiveUrl && !pdfRendering && <span className="text-xs text-green-600">✓ up to date</span>}
                  {pdfLiveUrl && (
                    <button onClick={() => window.open(pdfLiveUrl, '_blank')}
                            className="ml-auto text-xs text-gray-500 hover:text-gray-900 underline">
                      ↗ open full size
                    </button>
                  )}
                </div>
                {pdfLiveErr && (
                  <div className="bg-red-50 border border-red-200 text-red-800 rounded p-2 text-xs mb-2">
                    PDF render failed: {pdfLiveErr}
                  </div>
                )}
                {pdfLiveUrl ? (
                  <iframe title="Live cert PDF" src={pdfLiveUrl}
                          className="w-full border border-gray-300 rounded bg-white shadow-sm"
                          style={{ height: '80vh' }} />
                ) : (
                  <div className="border border-gray-200 rounded bg-gray-50 flex items-center justify-center text-sm text-gray-400"
                       style={{ height: '80vh' }}>
                    {pdfRendering ? 'Rendering the cert…' : 'The rendered cert PDF will appear here.'}
                  </div>
                )}
                <div className="text-[11px] text-gray-500 mt-2">
                  Live preview — updates about a second after you change the sample data, the engineer/customer view, or the cert type.
                </div>
              </div>
            </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Helpers — JSONC strip + structured-to-raw round-trip
// ────────────────────────────────────────────────────────────────────
function stripJsonc(src: string): string {
  let out = '';
  let i = 0;
  const n = src.length;
  let inStr = false;
  let strCh = '';
  while (i < n) {
    const c = src[i]; const nx = src[i + 1];
    if (inStr) {
      out += c;
      if (c === '\\' && i + 1 < n) { out += src[i + 1]; i += 2; continue; }
      if (c === strCh) inStr = false;
      i++; continue;
    }
    if (c === '"' || c === '\'') { inStr = true; strCh = c; out += c; i++; continue; }
    if (c === '/' && nx === '/') { while (i < n && src[i] !== '\n') i++; continue; }
    if (c === '/' && nx === '*') { i += 2; while (i < n && !(src[i] === '*' && src[i + 1] === '/')) i++; i += 2; continue; }
    out += c; i++;
  }
  return out;
}

// Serialize a SectionConfig back to .jsonc text. We preserve the header
// comment block of the original file (anything before the first `{`) and
// substitute the body with a pretty-printed JSON of the new config.
// This is intentionally simple: structured edits drop inline comments inside
// the object. Use raw mode if you need to keep inline comments.
function stringifyConfig(cfg: SectionConfig, originalRaw: string): string {
  // Find first '{' that's not inside a string or comment header.
  let i = 0;
  const stripped = stripJsonc(originalRaw);
  // Find header lines (everything before the first JSON token in the original).
  // Simpler: just keep lines before the first opening brace in the raw text.
  let firstBraceRawIdx = originalRaw.indexOf('{');
  if (firstBraceRawIdx < 0) firstBraceRawIdx = 0;
  const header = originalRaw.slice(0, firstBraceRawIdx);
  const body = JSON.stringify(cfg, null, 2);
  return header + body + '\n';
  void i; void stripped;
}
